import { message, Upload, UploadProps } from 'antd';
import useIM from 'hooks/business/im/useIM';
import { useUploadResource } from 'hooks/business/useUploadResource';
import { forwardRef } from 'react';
import { useTranslate } from '@tolgee/react';
import { platformUploadImageAcceptMap, platformUploadSizeLimitMap } from 'services/config/platform';
import getImage, { getImageFromBlob, getResourceBlobFromURL } from 'utils/getImage';
import toLowerCase from 'utils/toLowerCase';
import { uploadFile } from 'services/api/whatsapp';
import { get, isString, set } from 'lodash-es';
import { useMemoizedFn } from 'ahooks';
import useSendFileURL from 'hooks/business/useSendFileByURL';
import { ToolbarIcon } from '../ToolbarIcon';
import { useChatConversationContext } from 'views/Conversation/Chat/contexts/ChatConversationContext';
import { useCurrentChatBuyerLang } from 'hooks/business/im/chatPreference';
import { validatorLazadaImage } from 'utils/validator/lazadaImage';
import { logger } from 'services/logger';
import { uploadClass } from './styles';

export interface ImageUploadProps extends UploadProps {
  onSend?: () => void;
}

const ImageUpload = forwardRef<any, ImageUploadProps>((props: ImageUploadProps, ref) => {
  const { onSend, ...otherProps } = props;

  const { t } = useTranslate();

  const { currentConversation: conversation } = useChatConversationContext();

  const sendFileImage = useSendFileImage();

  if (!conversation) {
    return null;
  }

  const { controlStatus, channel: convChannel, topBarInfo } = conversation;

  const channel = topBarInfo?.channel || convChannel;
  const disabled = !controlStatus;

  return (
    <Upload
      className={uploadClass}
      ref={ref}
      accept={platformUploadImageAcceptMap[toLowerCase(channel)].map((_) => `.${_}`).join(',')}
      name="file"
      disabled={disabled}
      multiple
      showUploadList={false}
      beforeUpload={(file) => {
        const { [toLowerCase(channel)]: uploadSizeLimit } = platformUploadSizeLimitMap;

        if (file.size > uploadSizeLimit) {
          message.warn(
            t('upload.limitWarn', {
              size: `${(uploadSizeLimit / 1024 / 1024).toFixed(2)}M`,
            }),
          );

          return Upload.LIST_IGNORE;
        }

        if (channel !== 'LAZADA') {
          sendFileImage(file, onSend);

          return Upload.LIST_IGNORE;
        }

        return validatorLazadaImage(file).then((res) => {
          if (res) {
            message.warn(t(res));
          } else {
            sendFileImage(file, onSend);
          }
          return Upload.LIST_IGNORE;
        });
      }}
      {...otherProps}
    >
      <ToolbarIcon icon="image" disabled={disabled} />
    </Upload>
  );
});

export function useSendFileImage() {
  const { currentConversation: conversation } = useChatConversationContext();

  const { t } = useTranslate();
  const uploadFileURL = useSendFileURL();
  const uploadResource = useUploadResource();

  const { sendImage, sendAttachment } = useIM();

  const buyerLang = useCurrentChatBuyerLang(conversation.region);

  const sendFn = useMemoizedFn(
    async (file: File | string, onSend?: () => void, filename?: string, msgId?: string) => {
      if (!conversation) {
        return;
      }

      const { conversationId, storeId, region, channel } = conversation;
      const uploadWaPromise = (file: File) =>
        uploadFile(file).then((res) => {
          return {
            imgUrl: res.localUrl,
            externalImgUrl: res.url,
            width: 0,
            height: 0,
          };
        });

      let uploadPromise;

      const errorCatchHandler = (err: any) => {
        const data = err instanceof Error ? err.toString() : err;

        logger.info(
          `[ResourceUpload]: ${JSON.stringify({
            type: 'uploadFailed',
            data,
          })}`,
        );

        const failCause = get(err, 'failCause');
        if (['upload_failed', 'upload_timeout'].includes(failCause?.code)) {
          set(err, 'failCause', {
            ...failCause,
            reSendImage(id: string) {
              sendFn(file, onSend, filename, id);
            },
          });
        }
        return Promise.reject(err);
      };

      if (isString(file)) {
        uploadPromise = uploadFileURL({
          storeId,
          resourceType: 'IMAGE',
          url: file,
        })
          .then(({ localUrl, url, externalId, mimeType }) => {
            return getImage(localUrl).then((img) => {
              const res = {
                imgUrl:
                  channel === 'KWAI' ||
                  channel === 'MERCADO' ||
                  channel === 'TIKTOK' ||
                  channel === 'FACEBOOK'
                    ? localUrl || url
                    : url,
                externalImgUrl: url,
                width: img.width,
                height: img.height,
                externalId,
                mimeType,
              };
              if (conversation.channel !== 'KWAI') {
                return res;
              }
              return getResourceBlobFromURL(localUrl).then((blob) => {
                return {
                  ...res,
                  imgUrl: localUrl || url,
                  size: blob.size,
                };
              });
            });
          })
          .catch(errorCatchHandler);
      } else {
        uploadPromise = uploadResource({
          file,
          storeId,
          region,
          fileType: 'IMAGE',
          reqSource: conversation.channel === 'MERCADO' ? 'ATTACHMENT' : 'CONVERSATION',
        })
          .then(({ url, localUrl, externalId }) => {
            const getImageSize = () => {
              // 优先加载本地文件，加快图片发送速度。
              return getImageFromBlob(file)
                .catch(() =>
                  channel === 'SHOPEE' ? Promise.resolve({ width: 0, height: 0 }) : getImage(url),
                )
                .catch(() => (localUrl ? getImage(localUrl) : Promise.reject(new Error())));
            };

            return getImageSize().then((img) => {
              const { width, height } = img;

              return {
                imgUrl:
                  conversation.channel === 'KWAI' ||
                  conversation.channel === 'MERCADO' ||
                  conversation.channel === 'TIKTOK' ||
                  conversation.channel === 'FACEBOOK'
                    ? localUrl || url
                    : url,
                externalImgUrl: url,
                width,
                height,
                contentLength: file.size,
                externalId,
                mimeType: file.type,
              };
            });
          })
          .catch(errorCatchHandler);
      }

      if (channel === 'MERCADO') {
        const langTextMap: any = {
          'zh-CN': '请查收您的发票',
          'en-US': 'Please check your invoice',
          es: 'Por favor revise su factura',
          id: 'Silakan periksa faktur Anda',
          pt: 'Por favor, verifique a sua factura',
          th: 'กรุณาตรวจสอบใบแจ้งหนี้ของคุณ',
          tl: 'Please pa-check po ako ng invoice',
          vi: 'Vui lòng kiểm tra hóa đơn của bạn',
        };
        const regionTextMap: any = {
          BR: langTextMap.pt,
          MX: langTextMap.es,
          AR: langTextMap.es,
        };
        const text = langTextMap[buyerLang] || regionTextMap[conversation.region] || ' ';
        uploadPromise
          .then(({ imgUrl, externalImgUrl }) => {
            sendAttachment(
              conversation,
              {
                text,
                subject: '',
                originalText: text,
                attachments: [
                  {
                    filename: externalImgUrl,
                    originalFilename: externalImgUrl,
                    url: imgUrl,
                    size: '',
                    type: 'png',
                  },
                ],
              },
              undefined,
              false,
            );
          })
          .catch((err) => {
            const failCause = get(err, 'failCause');
            if (['upload_failed', 'upload_timeout'].includes(failCause?.code)) {
              message.error(t('ViewsChat.上传失败'));
            }
          });
      } else {
        const url = isString(file) ? file : URL.createObjectURL(file);
        sendImage(
          conversationId,
          channel === 'WA_BIZ' ? uploadWaPromise(file as File) : uploadPromise,
          url,
          { localUpload: true },
          msgId?.replace('nim-', ''),
        ).finally(() => {
          onSend && onSend();
          if (url) {
            URL.revokeObjectURL(url);
          }
        });
      }
    },
  );

  return sendFn;
}

export default ImageUpload;
